import { takeEvery, call, put } from 'redux-saga/effects';
import {
    fetchCategoriesStart,
    fetchCategoriesSuccess,
    fetchCategoriesFail,
    createCategoryStart,
    createCategorySuccess,
    createCategoryFail,
    updateCategoryStart,
    updateCategorySuccess,
    updateCategoryFail,
    fetchPromptsStart,
    fetchPromptsSuccess,
    fetchPromptsFail,
    createPromptStart,
    createPromptSuccess,
    createPromptFail,
    updatePromptStart,
    updatePromptSuccess,
    updatePromptFail,
    fetchReactionsStart,
    fetchReactionsSuccess,
    fetchReactionsFail,
    fetchUserReactionStart,
    fetchUserReactionSuccess,
    fetchUserReactionFail,
    fetchPromptDataModalsSuccess,
    fetchPromptDataModalsFail,
    createPromptDataModalSuccess,
    createPromptDataModalFail,
    updatePromptDataModalSuccess,
    updatePromptDataModalFail,
    fetchPromptDataModalsStart,
    createPromptDataModalStart,
    updatePromptDataModalStart,
    fetchPromptDetailSuccess,
    fetchPromptDetailFail,
    fetchPromptDetailStart,
    importPromptsSuccess,
    importPromptsFail,
    importPromptsStart,
} from '../slices/promptsSlice';
import { getRequest, postRequest, putRequest, patchRequest, uploadFiles } from '../../network';
import {
    createCategory,
    getCategories,
    updateCategory,
    createPrompt,
    getPrompts,
    updatePrompt,
    getReactionsForPrompt,
    getUserReaction,
    getDataModals,
    createDataModal,
    updateDataModal,
    getPromptDetail,
    importPrompts,
} from '../apis';

// Generic handler for Prompts API calls
function* promptsSaga(action) {
    const { endpoint, method = 'get', successAction, failAction, payload = null, callback = null } = action.payload;
    const requestFunction =
        method === 'post' ? postRequest : method === 'put' ? putRequest : method === 'patch' ? patchRequest : getRequest;

    try {
        const response = yield call(requestFunction, endpoint, payload);
        if (callback && typeof callback === 'function') {
            yield call(callback, true);
        }
        yield put(successAction(response));
    } catch (error) {
        if (callback && typeof callback === 'function') {
            let errorMessage = "Something went wrong";
            if (error.response && error.response.data) {
                const data = error.response.data;
                if (data.message && data.errors) {
                    errorMessage = `${data.message}: ${data.errors.join(', ')}`;
                } else if (data.message) {
                    errorMessage = data.message;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            yield call(callback, false, errorMessage);
        }
        yield put(failAction(error.message));
    }
}

function* uploadPromptFileSaga(action) {
  const { endpoint, successAction, failAction, payload, callback } =
    action.payload;
  const file = payload?.file || payload?.payload;
  try {
    const response = yield call(uploadFiles, endpoint, file, {}, { timeout: 600000 });

    // Clear UI loading before Redux dispatch so a middleware/reducer error cannot strand "Uploading…"
    if (callback && typeof callback === "function") {
      yield call(callback, true, response);
    }
    yield put(successAction(response ?? null));
  } catch (error) {
    const message =
      error.response?.data?.message || error.message || "Upload failed";

    if (callback && typeof callback === "function") {
      yield call(callback, false, message);
    }
    yield put(failAction(message));
  }
}

function* fetchCategoriesSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: getCategories,
            payload: action.payload,
            successAction: fetchCategoriesSuccess,
            failAction: fetchCategoriesFail,
        },
    });
}

function* createCategorySaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: createCategory,
            method: 'post',
            payload: action.payload.data,
            callback: action.payload.callback,
            successAction: createCategorySuccess,
            failAction: createCategoryFail,
        },
    });
}

function* updateCategorySaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: updateCategory.replace(':id', action.payload.id),
            method: 'put',
            payload: action.payload.data,
            callback: action.payload.callback,
            successAction: updateCategorySuccess,
            failAction: updateCategoryFail,
        },
    });
}

function* fetchDataModalsSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: getDataModals,
            payload: action.payload,
            successAction: fetchPromptDataModalsSuccess,
            failAction: fetchPromptDataModalsFail,
        },
    });
}

function* createDataModalSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: createDataModal,
            method: 'post',
            payload: action.payload.data,
            callback: action.payload.callback,
            successAction: createPromptDataModalSuccess,
            failAction: createPromptDataModalFail,
        },
    });
}

function* updateDataModalSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: updateDataModal.replace(':id', action.payload.id),
            method: 'put',
            payload: action.payload.data,
            callback: action.payload.callback,
            successAction: updatePromptDataModalSuccess,
            failAction: updatePromptDataModalFail,
        },
    });
}

function* fetchPromptsSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: getPrompts,
            payload: action.payload,
            successAction: fetchPromptsSuccess,
            failAction: fetchPromptsFail,
        },
    });
}

function* fetchPromptDetailSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: getPromptDetail.replace(':id', action.payload.id),
            successAction: fetchPromptDetailSuccess,
            failAction: fetchPromptDetailFail,
        },
    });
}

function* createPromptSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: createPrompt,
            method: 'post',
            payload: action.payload.data,
            callback: action.payload.callback,
            successAction: createPromptSuccess,
            failAction: createPromptFail,
        },
    });
}

function* updatePromptSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: updatePrompt.replace(':id', action.payload.id),
            method: 'put',
            payload: action.payload.data,
            callback: action.payload.callback,
            successAction: updatePromptSuccess,
            failAction: updatePromptFail,
        },
    });
}

function* fetchReactionsSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: getReactionsForPrompt.replace(':promptID', action.payload.promptID),
            successAction: fetchReactionsSuccess,
            failAction: fetchReactionsFail,
        },
    });
}

function* fetchUserReactionSaga(action) {
    yield promptsSaga({
        payload: {
            endpoint: getUserReaction
                .replace(':promptID', action.payload.promptID)
                .replace(':userID', action.payload.userID),
            successAction: fetchUserReactionSuccess,
            failAction: fetchUserReactionFail,
        },
    });
}

function* importPromptsSaga(action) {
  yield uploadPromptFileSaga({
    payload: {
      endpoint: importPrompts,
      method: "post",
      payload: action.payload,
      callback: action.payload.callback,
      successAction: importPromptsSuccess,
      failAction: importPromptsFail,
    },
  });
}

// Watcher saga
export function* watchPrompts() {
    yield takeEvery(fetchCategoriesStart.type, fetchCategoriesSaga);
    yield takeEvery(createCategoryStart.type, createCategorySaga);
    yield takeEvery(updateCategoryStart.type, updateCategorySaga);
    yield takeEvery(fetchPromptsStart.type, fetchPromptsSaga);
    yield takeEvery(createPromptStart.type, createPromptSaga);
    yield takeEvery(updatePromptStart.type, updatePromptSaga);
    yield takeEvery(fetchPromptDataModalsStart.type, fetchDataModalsSaga);
    yield takeEvery(createPromptDataModalStart.type, createDataModalSaga);
    yield takeEvery(updatePromptDataModalStart.type, updateDataModalSaga);
    yield takeEvery(fetchReactionsStart.type, fetchReactionsSaga);
    yield takeEvery(fetchUserReactionStart.type, fetchUserReactionSaga);
    yield takeEvery(fetchPromptDetailStart.type, fetchPromptDetailSaga);
    yield takeEvery(importPromptsStart.type, importPromptsSaga);
}